// =============================================================================
// Authenticator Modal Component
// =============================================================================
// Modal for Microsoft Authenticator / TOTP setup and verification
// Used to protect sensitive data access (ACH/routing numbers)
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/auth.service';
import CodeInput from './CodeInput';

// =============================================================================
// Types
// =============================================================================

export interface AuthenticatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (expiresAt: Date, code?: string) => void;
  title?: string;
  description?: string;
}

type ModalState = 'loading' | 'setup' | 'verify' | 'error';

// =============================================================================
// Component
// =============================================================================

export function AuthenticatorModal({
  isOpen,
  onClose,
  onVerified,
  title = 'Verification Required',
  description = 'Enter the code from your authenticator app to access sensitive information.',
}: AuthenticatorModalProps) {
  const [state, setState] = useState<ModalState>('loading');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Check TOTP status when modal opens
  const checkStatus = useCallback(async () => {
    if (!isOpen) return;

    setState('loading');
    setError('');
    setCode('');

    try {
      const status = await authService.getTotpStatus();

      if (status.success) {
        // Check if user has valid session already
        if (status.hasValidSession && status.sessionExpiresAt) {
          // Already verified, just pass through
          onVerified(new Date(status.sessionExpiresAt));
          return;
        }

        if (status.enabled) {
          // User has TOTP enabled, show verify screen
          setState('verify');
        } else {
          // User needs to set up TOTP first
          await initiateSetup();
        }
      } else {
        setError(status.error || 'Failed to check authenticator status');
        setState('error');
      }
    } catch (err) {
      console.error('Error checking TOTP status:', err);
      setError('Failed to check authenticator status');
      setState('error');
    }
  }, [isOpen, onVerified]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Initiate TOTP setup
  const initiateSetup = async () => {
    try {
      const response = await authService.initiateTotpSetup();

      if (response.success && response.qrCodeDataUrl && response.secret) {
        setQrCodeUrl(response.qrCodeDataUrl);
        setSecret(response.secret);
        setState('setup');
      } else {
        setError(response.error || 'Failed to setup authenticator');
        setState('error');
      }
    } catch (err) {
      console.error('Error initiating TOTP setup:', err);
      setError('Failed to setup authenticator');
      setState('error');
    }
  };

  // Handle code submission
  const handleSubmit = async (submittedCode: string) => {
    if (submittedCode.length !== 6 || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      let response;

      if (state === 'setup') {
        // Complete setup
        response = await authService.completeTotpSetup(submittedCode);
      } else {
        // Verify code
        response = await authService.verifyTotp(submittedCode);
      }

      if (response.success && response.expiresAt) {
        onVerified(new Date(response.expiresAt), submittedCode);
      } else {
        setError(response.error || 'Invalid code. Please try again.');
        setCode('');
      }
    } catch (err) {
      console.error('Error verifying code:', err);
      setError('Verification failed. Please try again.');
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format secret for display (groups of 4)
  const formatSecret = (s: string): string => {
    return s.match(/.{1,4}/g)?.join(' ') || s;
  };

  // Handle modal close
  const handleClose = () => {
    setCode('');
    setError('');
    setState('loading');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="border-b border-gray-100 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button
                onClick={handleClose}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Loading State */}
            {state === 'loading' && (
              <div className="flex flex-col items-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                <p className="mt-4 text-sm text-gray-500">Checking authenticator status...</p>
              </div>
            )}

            {/* Error State */}
            {state === 'error' && (
              <div className="flex flex-col items-center py-6">
                <div className="mb-4 rounded-full bg-red-100 p-3">
                  <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="mb-4 text-center text-sm text-gray-600">{error}</p>
                <button
                  onClick={checkStatus}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Setup State - Show QR Code */}
            {state === 'setup' && (
              <div className="flex flex-col items-center">
                <div className="mb-4 rounded-full bg-blue-100 p-3">
                  <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>

                <h3 className="mb-2 text-base font-medium text-gray-900">Set Up Authenticator</h3>
                <p className="mb-6 text-center text-sm text-gray-500">
                  Scan this QR code with Microsoft Authenticator, Google Authenticator, or any TOTP app.
                </p>

                {/* QR Code */}
                <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
                  <img
                    src={qrCodeUrl}
                    alt="QR Code for authenticator app"
                    className="h-48 w-48"
                  />
                </div>

                {/* Manual Entry Toggle */}
                <button
                  onClick={() => setShowManualEntry(!showManualEntry)}
                  className="mb-4 text-sm text-primary-600 hover:text-primary-700"
                >
                  {showManualEntry ? 'Hide manual entry key' : "Can't scan? Enter key manually"}
                </button>

                {showManualEntry && (
                  <div className="mb-4 w-full rounded-lg bg-gray-50 p-3">
                    <p className="mb-1 text-xs font-medium text-gray-500">Manual Entry Key:</p>
                    <p className="font-mono text-sm text-gray-900 select-all">{formatSecret(secret)}</p>
                  </div>
                )}

                {/* App Download Links */}
                <div className="mb-4 w-full">
                  <p className="text-xs text-gray-500 text-center mb-3">
                    Don't have an authenticator app? Download one:
                  </p>
                  <div className="flex justify-center gap-6">
                    {/* Microsoft Authenticator */}
                    <a
                      href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 text-center hover:opacity-80 transition-opacity"
                    >
                      <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600">Microsoft</span>
                    </a>
                    {/* Google Authenticator */}
                    <a
                      href="https://support.google.com/accounts/answer/1066447"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 text-center hover:opacity-80 transition-opacity"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 4.8a7.2 7.2 0 110 14.4 7.2 7.2 0 010-14.4zm0 2.4a4.8 4.8 0 100 9.6 4.8 4.8 0 000-9.6z"/>
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600">Google</span>
                    </a>
                  </div>
                </div>

                {/* Divider */}
                <div className="mb-4 w-full border-t border-gray-200" />

                {/* Code Input */}
                <p className="mb-3 text-sm text-gray-600">
                  Enter the 6-digit code from your app:
                </p>

                <CodeInput
                  value={code}
                  onChange={setCode}
                  onComplete={handleSubmit}
                  error={!!error}
                  disabled={isSubmitting}
                />

                {error && (
                  <p className="mt-3 text-sm text-red-600">{error}</p>
                )}

                {isSubmitting && (
                  <p className="mt-3 text-sm text-gray-500">Verifying...</p>
                )}
              </div>
            )}

            {/* Verify State - Code Entry Only */}
            {state === 'verify' && (
              <div className="flex flex-col items-center">
                <div className="mb-4 rounded-full bg-primary-100 p-3">
                  <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>

                <p className="mb-6 text-center text-sm text-gray-500">{description}</p>

                {/* Code Input */}
                <CodeInput
                  value={code}
                  onChange={setCode}
                  onComplete={handleSubmit}
                  error={!!error}
                  disabled={isSubmitting}
                />

                {error && (
                  <p className="mt-3 text-sm text-red-600">{error}</p>
                )}

                {isSubmitting && (
                  <p className="mt-3 text-sm text-gray-500">Verifying...</p>
                )}

                {/* Help text */}
                <p className="mt-6 text-center text-xs text-gray-400">
                  Open your authenticator app (Microsoft Authenticator, Google Authenticator, etc.) to get your code.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthenticatorModal;
